import * as Sentry from '@sentry/browser'
import { AxiosHeaders, type InternalAxiosRequestConfig } from 'axios'

import { captureException } from './monitoring'

type DiagnosticData = Record<string, string | number | boolean | null>
type AuthOutcome = 'bearer' | 'optional_auth' | 'blocked'
type FailurePhase = 'session_failure' | 'forbidden'

export interface AuthRequestDiagnostics {
  startedAt: number
  data: DiagnosticData
}

// Limit new diagnostic events, not just issue grouping. Breadcrumbs still
// record every outcome. Existing caller-level error reporting is unchanged.
const REPORT_INTERVAL_MS = 60_000
const reports: Partial<Record<FailurePhase, { at: number; suppressed: number }>> = {}
let requestSequence = 0
let appSessionStatus = 'unknown'
let lastAuthEvent: { event: string; at: number } | undefined

// Never serialize an Amplify/Axios error or its cause: those can carry tokens,
// credentials, request headers, or customer data. Only known reasons survive.
const SAFE_ERROR_MESSAGES = new Set([
  'No current user',
  'No current user.',
  'Network error',
  'Network Error',
  'Failed to fetch',
  'Network request failed',
  'Refresh Token has expired',
  'Refresh Token has been revoked',
  'Invalid Refresh Token',
  'Access Token has been revoked',
  'User is disabled.',
  'User does not exist.',
  'Password reset required for the user',
  'User is not authenticated',
  'No User Pool in the configuration.',
  'Cannot retrieve a new session. Please authenticate.',
  'Local storage is missing an ID Token, Please authenticate',
])
const SAFE_ERROR_NAMES = new Set([
  'Error',
  'TypeError',
  'NetworkError',
  'NotAuthorizedException',
  'UserNotFoundException',
  'TooManyRequestsException',
  'PasswordResetRequiredException',
  'SessionIdentityMismatchError',
])

const describeError = (error: unknown): DiagnosticData => {
  const details =
    error && typeof error === 'object' ? (error as { name?: unknown; code?: unknown; message?: unknown }) : {}
  const name = typeof details.code === 'string' ? details.code : details.name
  const message = typeof error === 'string' ? error : details.message
  return {
    error_name: typeof name === 'string' && SAFE_ERROR_NAMES.has(name) ? name : typeof error,
    error_reason: typeof message === 'string' && SAFE_ERROR_MESSAGES.has(message) ? message : 'unrecognized',
  }
}

const safely = (record: () => void) => {
  try {
    record()
  } catch {
    // Diagnostics must never change request, authentication, or routing behavior.
  }
}

const breadcrumb = (message: string, data: DiagnosticData) =>
  Sentry.addBreadcrumb({ category: 'admin.auth', message, level: 'info', data: { ...data } })

const environment = (): DiagnosticData => ({
  app_session_status: appSessionStatus,
  online: typeof navigator === 'undefined' ? null : navigator.onLine,
  visibility: typeof document === 'undefined' ? 'unknown' : document.visibilityState,
  last_auth_event: lastAuthEvent?.event ?? null,
  last_auth_event_age_ms: lastAuthEvent ? Date.now() - lastAuthEvent.at : null,
})

// Inspect only this request's configuration. Never retain the header value or
// perform a second session lookup; neither snapshot proves what reached Gateway.
const headerState = (headers?: InternalAxiosRequestConfig['headers']): string => {
  if (!headers) return 'unknown'
  try {
    const value = AxiosHeaders.from(headers).get('Authorization')
    if (value == null || value === false || (typeof value === 'string' && !value.trim())) return 'missing'
    if (typeof value !== 'string') return 'other'
    if (value.trim() === 'OPTIONAL_AUTH') return 'optional'
    if (/^Bearer(?:\s|$)/i.test(value.trim())) {
      return value.trim().replace(/^Bearer\s*/i, '').length ? 'bearer_nonempty' : 'bearer_empty'
    }
    return 'other'
  } catch {
    return 'unknown'
  }
}

const report = (phase: FailurePhase, data: DiagnosticData) => {
  const previous = reports[phase]
  if (previous && Date.now() - previous.at < REPORT_INTERVAL_MS) {
    previous.suppressed++
    return
  }
  const suppressed = previous?.suppressed ?? 0
  reports[phase] = { at: Date.now(), suppressed: 0 }
  const error = new Error(`Admin auth: ${phase}`)
  error.name = 'AdminAuthDiagnostic'
  const context: DiagnosticData = { ...data, ...environment(), suppressed_events: suppressed }
  captureException(error, {
    tags: [
      { key: 'auth_diagnostic', value: phase },
      { key: 'auth_outcome', value: String(data.auth_outcome) },
      { key: 'auth_diagnostic_version', value: '2' },
      // Fixed classifications only. Tags make these fields aggregatable without
      // reading individual contexts. Verify ingestion under Sentry's scrubbers.
      ...[
        'credential_present',
        'request_header_state',
        'failed_header_state',
        'app_session_status_before',
        'app_session_status',
        'error_reason',
        'last_auth_event',
      ].map((key) => ({ key, value: String(context[key] ?? 'unknown') })),
    ],
    context: Object.fromEntries(Object.entries(context).map(([key, value]) => [key, String(value)])),
    fingerprint: ['admin-auth-diagnostic', phase],
  })
}

export const startAuthDiagnostics = (config: InternalAxiosRequestConfig): AuthRequestDiagnostics | undefined => {
  try {
    const url = new URL(
      /^https?:\/\//i.test(config.url ?? '')
        ? config.url!
        : `${config.baseURL?.replace(/\/$/, '')}/${config.url?.replace(/^\//, '')}`
    )
    if (!/^\/(?:api\/v1\/)?admin\//.test(url.pathname)) return undefined
    const startedAt = Date.now()
    return {
      startedAt,
      data: {
        client_request_id: `${startedAt.toString(36)}-${++requestSequence}`,
        // Keep the pathname for diagnosis, excluding query strings and fragments.
        path: url.pathname,
        method: (config.method ?? 'GET').toUpperCase(),
        app_session_status_before: appSessionStatus,
        credential_present: 'unknown',
      },
    }
  } catch {
    return undefined
  }
}

export const recordAuthCredentialPresence = (diagnostic: AuthRequestDiagnostics | undefined, present: boolean) => {
  if (!diagnostic) return
  safely(() => {
    diagnostic.data.credential_present = present
  })
}

export const finishAuthDiagnostics = (
  diagnostic: AuthRequestDiagnostics | undefined,
  outcome: AuthOutcome,
  error?: unknown,
  headers?: InternalAxiosRequestConfig['headers']
) => {
  if (!diagnostic) return
  safely(() => {
    Object.assign(diagnostic.data, {
      auth_outcome: outcome,
      request_header_state: outcome === 'blocked' ? 'not_sent' : headerState(headers),
      session_lookup_ms: Date.now() - diagnostic.startedAt,
      ...environment(),
      ...(outcome === 'bearer' ? {} : describeError(error)),
    })
    breadcrumb('session_lookup', diagnostic.data)
    if (outcome !== 'bearer') report('session_failure', diagnostic.data)
  })
}

export const recordAuthForbidden = (
  diagnostic: AuthRequestDiagnostics | undefined,
  gatewayRequestId?: string,
  headers?: InternalAxiosRequestConfig['headers']
) => {
  if (!diagnostic) return
  safely(() => {
    const data = {
      ...diagnostic.data,
      ...environment(),
      failed_header_state: headerState(headers),
      request_elapsed_ms: Date.now() - diagnostic.startedAt,
      gateway_request_id:
        gatewayRequestId && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(gatewayRequestId)
          ? gatewayRequestId
          : null,
    }
    breadcrumb('http_403', data)
    report('forbidden', data)
  })
}

export const recordAuthState = (status: string) => {
  appSessionStatus = status
  safely(() => breadcrumb('app_session_state', { app_session_status: status }))
}

export const recordAuthEvent = (event: string, error?: unknown) => {
  if (!['signIn', 'signOut', 'tokenRefresh', 'tokenRefresh_failure'].includes(event)) return
  lastAuthEvent = { event, at: Date.now() }
  safely(() =>
    breadcrumb(event, {
      app_session_status: appSessionStatus,
      ...(event === 'tokenRefresh_failure' ? describeError(error) : {}),
    })
  )
}
